import { useCallback, useEffect, useRef, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useFlags } from "launchdarkly-react-client-sdk";

import { neutral, success } from "app/themes/colors";
import { AMBIENT_SCRIBE_VERIFIED_MICROPHONES } from "constants/feature-flags";
import Dialog from "components/dialog";
import MicrophoneFormField from "components/microphone-form-field";
import useSpeechToTextStreaming from "components/word-processor-field/speech-to-text/use-speech-to-text-streaming.hook";
import WaveVolumeIndicator from "features/medical-scribe/components/wave-volume-indicator.component";
import { RECORDING_STATE_IN_PROGRESS } from "features/medical-scribe/medical-scribe-constants";
import { useAudioAnalyzer } from "features/medical-scribe/medical-scribe-recording-widget/use-audio-analyzer.hook";
import {
  PSEUDO_DEVICE_IDS,
  createMicrophoneFingerprint,
  fingerprintToInput,
  requestMicrophonePermissionForEnumeration
} from "features/medical-scribe/microphone.utils";
import { saveVerifiedMicrophone } from "features/medical-scribe/microphone-storage";

const TEST_TIMEOUT_MS = 120_000;
const WORD_THRESHOLD = 10;

function wordsNoun(count: number): string {
  return count === 1 ? "word" : "words";
}

type TestStatus = "idle" | "recording" | "stopped" | "success" | "failed";
type MicPermission = "prompt" | "granted" | "denied";

interface CurrentSettings {
  microphone: string;
  speaker: string;
  showTranscriptionPreview?: boolean;
  autoGenerateSOAPNotes?: boolean;
  autoFindRelatedChartNote?: boolean;
}

interface MicrophoneSetupModalProps {
  onClose: () => void;
  onComplete: (micDeviceId: string) => void;
  onMicrophoneChange?: (micDeviceId: string) => void;
  currentSettings: CurrentSettings;
  userId: number;
}

export default function MicrophoneSetupModal({
  onClose,
  onComplete,
  onMicrophoneChange,
  currentSettings,
  userId
}: MicrophoneSetupModalProps) {
  const theme = useTheme();
  const flags = useFlags();
  const isVerifiedMicrophonesEnabled = Boolean(
    flags[AMBIENT_SCRIBE_VERIFIED_MICROPHONES]
  );

  const [micPermission, setMicPermission] = useState<MicPermission>("prompt");
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedMic, setSelectedMic] = useState(currentSettings.microphone);
  const [isRecording, setIsRecording] = useState(false);
  const [volume, setVolume] = useState(0);
  const [isMicActive, setIsMicActive] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [transcriptText, setTranscriptText] = useState("");
  const [testStatus, setTestStatus] = useState<TestStatus>("idle");
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  // Request microphone permission on mount so the browser dialog fires immediately
  // and the dropdown has labeled devices when permission is granted.
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!navigator.mediaDevices) {
        if (mounted) setMicPermission("denied");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        // Stop tracks right away — we only needed the grant, not the stream.
        // Keeping them open would show the "mic in use" indicator prematurely.
        stream.getTracks().forEach((t) => t.stop());
        if (mounted) setMicPermission("granted");
      } catch {
        if (mounted) setMicPermission("denied");
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Ref-tracked word count to avoid stale closure in onAppendText
  const wordCountRef = useRef(0);

  // Component owns the stream (getUserMedia + track cleanup).
  // useAudioAnalyzer owns the AudioContext, AnalyserNode, and RAF loop.
  const streamRef = useRef<MediaStream | null>(null);

  const { startAnalysis, stopAnalysis } = useAudioAnalyzer({
    onVolumeChange: (v, active) => {
      setVolume(v);
      setIsMicActive(active);
    },
    debounceMs: 0
  });

  const stopVolumeMonitoring = useCallback(() => {
    stopAnalysis();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, [stopAnalysis]);

  // Gets a mic stream and starts the analyser loop.
  // Called directly from click handlers (not useEffect) to guarantee execution order:
  // our stream is established BEFORE setIsRecording(true) triggers the STT hook's
  // getUserMedia, which eliminates the race condition that caused volume to be 0.
  const startVolumeMonitoring = useCallback(async () => {
    stopVolumeMonitoring();
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        selectedMic
          ? { audio: { deviceId: { exact: selectedMic } } }
          : { audio: true }
      );
      streamRef.current = stream;
      await startAnalysis(stream);
    } catch (error) {
      // If selected device is unavailable, gracefully fall back to default mic.
      if (selectedMic) {
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            audio: true
          });
          streamRef.current = fallbackStream;
          await startAnalysis(fallbackStream);
          return;
        } catch (fallbackError) {
          console.warn(
            "Fallback microphone stream request failed",
            fallbackError
          );
          // mic access denied — bars stay flat (isMicActive remains false), which is correct
        }
      }
    }
  }, [selectedMic, startAnalysis, stopVolumeMonitoring]);

  const stopTest = useCallback(
    (result: "success" | "failed") => {
      setIsRecording(false);
      stopVolumeMonitoring();
      setTestStatus(result);
    },
    [stopVolumeMonitoring]
  );

  const handleAppendText = useCallback(
    (text: string) => {
      const newWords = text.trim().split(/\s+/).filter(Boolean).length;
      if (newWords === 0) return;
      wordCountRef.current += newWords;
      setWordCount(wordCountRef.current);
      setTranscriptText((prev) => (prev ? prev + text : text.trimStart()));
      if (wordCountRef.current >= WORD_THRESHOLD) {
        stopTest("success");
      }
    },
    [stopTest]
  );

  // enabled: isRecording — hook is inactive until recording, cleans up when it stops
  useSpeechToTextStreaming({
    isRecording,
    onAppendText: handleAppendText,
    enabled: isRecording,
    microphoneDeviceId: selectedMic
  });

  // 120-second failure timeout
  useEffect(() => {
    if (!isRecording) return;
    const id = setTimeout(() => stopTest("failed"), TEST_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [isRecording, stopTest]);

  // Seconds elapsed for countdown display
  useEffect(() => {
    if (!isRecording) {
      setSecondsElapsed(0);
      return;
    }
    const id = setInterval(() => setSecondsElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  // Unmount: release the AnalyserNode stream.
  // The STT hook handles its own cleanup on unmount (hook lines 984-992).
  useEffect(() => {
    return () => stopVolumeMonitoring();
  }, [stopVolumeMonitoring]);

  // Start monitoring FIRST, then enable the STT hook.
  // The async ordering ensures our stream is acquired before the STT hook's getUserMedia.
  const handleStartTest = useCallback(async () => {
    setStep(2);
    setTestStatus("recording");
    await startVolumeMonitoring();
    setIsRecording(true);
  }, [startVolumeMonitoring]);

  const handleStop = useCallback(() => {
    setIsRecording(false);
    stopVolumeMonitoring();
    setTestStatus("stopped");
  }, [stopVolumeMonitoring]);

  const handleBack = useCallback(() => {
    setIsRecording(false);
    stopVolumeMonitoring();
    wordCountRef.current = 0;
    setWordCount(0);
    setTranscriptText("");
    setTestStatus("idle");
    setStep(1);
  }, [stopVolumeMonitoring]);

  const handleRetry = useCallback(() => {
    wordCountRef.current = 0;
    setWordCount(0);
    setTranscriptText("");
    setIsRecording(false);
    stopVolumeMonitoring();
    setTestStatus("recording");
    // 50ms gap lets the STT hook's cleanup effect fire before restarting
    setTimeout(async () => {
      await startVolumeMonitoring();
      setIsRecording(true);
    }, 50);
  }, [startVolumeMonitoring, stopVolumeMonitoring]);

  const performSave = useCallback(async () => {
    if (!isVerifiedMicrophonesEnabled) {
      return;
    }
    // Pseudo device IDs ("default", "communications") are OS-level proxies that can
    // point to different hardware at any time — resolve to the actual physical device
    // before saving so future fingerprint matching works correctly.
    let targetDeviceId = selectedMic;
    if (PSEUDO_DEVICE_IDS.includes(targetDeviceId?.toLowerCase())) {
      try {
        const { activeDeviceId } =
          await requestMicrophonePermissionForEnumeration(targetDeviceId);
        if (
          activeDeviceId &&
          !PSEUDO_DEVICE_IDS.includes(activeDeviceId.toLowerCase())
        ) {
          targetDeviceId = activeDeviceId;
        }
      } catch {
        // If enumeration fails, continue with the original deviceId
      }
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const device = devices.find(
        (d) =>
          d.kind === "audioinput" &&
          d.deviceId === targetDeviceId &&
          !PSEUDO_DEVICE_IDS.includes(d.deviceId.toLowerCase())
      );
      if (device) {
        const fp = createMicrophoneFingerprint(device);
        saveVerifiedMicrophone(userId, {
          ...fingerprintToInput(fp),
          verified_at: new Date().toISOString()
        });
      }
    } catch {
      // enumerateDevices can fail (permissions revoked); silently no-op.
    }
  }, [isVerifiedMicrophonesEnabled, selectedMic, userId]);

  // Always points to the latest performSave closure; used by the useEffect below
  // to avoid adding performSave to the effect's dependency array (which would
  // re-fire the effect on every parent re-render that recreates the callback).
  const performSaveRef = useRef<() => Promise<void>>();
  performSaveRef.current = performSave;

  // Auto-trigger save as soon as the test succeeds so configuration is persisted
  // regardless of which button (or X) the user uses to dismiss the modal.
  useEffect(() => {
    if (testStatus === "success") {
      void performSaveRef.current?.();
    }
  }, [testStatus]);

  const handleDialogClose = useCallback(
    (_event: object, _reason: string) => {
      if (testStatus === "success") {
        onComplete(selectedMic);
      }
      onClose();
    },
    [testStatus, onComplete, onClose, selectedMic]
  );

  const secondsRemaining = Math.max(0, 120 - secondsElapsed);
  const progressValue = Math.min(100, (wordCount / WORD_THRESHOLD) * 100);
  const wordsStillNeeded = Math.max(0, WORD_THRESHOLD - wordCount);

  return (
    <Dialog
      title="Set Up Your Microphone"
      open={true}
      onClose={handleDialogClose}
      minWidth={460}
      sx={{ zIndex: theme.zIndex.snackbar + 10 }}
    >
      {step === 1 && (
        <Stack gap={3} pt={1}>
          <Typography variant="body3" color="text.secondary">
            Choosing the right microphone ensures Suno accurately captures your
            visit.
          </Typography>

          {micPermission === "prompt" && (
            <Stack direction="row" alignItems="center" gap={1.5} py={1}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Waiting for microphone access…
              </Typography>
            </Stack>
          )}

          {micPermission === "denied" && (
            <Alert severity="warning">
              Microphone access is unavailable. Please check that your browser
              has microphone permissions enabled, then reopen this dialog.
            </Alert>
          )}

          {micPermission === "granted" && (
            <MicrophoneFormField
              label="Microphone"
              value={selectedMic}
              onChange={(value: string) => {
                setSelectedMic(value);
                onMicrophoneChange?.(value);
              }}
              menuProps={{ style: { zIndex: theme.zIndex.snackbar + 11 } }}
            />
          )}

          <Stack direction="row" justifyContent="flex-end" gap={1.5}>
            <Button variant="text" onClick={onClose}>
              Skip for now
            </Button>
            {micPermission === "granted" && (
              <Button
                variant="contained"
                onClick={handleStartTest}
                disabled={!selectedMic}
                endIcon={<ArrowForwardIcon />}
              >
                Test Microphone
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {step === 2 && (
        <Stack gap={2.5} pt={1}>
          {testStatus === "recording" && (
            <>
              <Typography
                variant="body3"
                color="text.secondary"
                sx={{ fontSize: "32px", lineHeight: 1.2, fontWeight: 700 }}
              >
                {wordsStillNeeded > 0 ? (
                  wordCount === 0 ? (
                    <>
                      Please&nbsp;speak at&nbsp;least {WORD_THRESHOLD}&nbsp;
                      {wordsNoun(WORD_THRESHOLD)} to&nbsp;finish the microphone
                      test.
                    </>
                  ) : (
                    <>
                      Please&nbsp;speak at&nbsp;least {wordsStillNeeded} more{" "}
                      {wordsNoun(wordsStillNeeded)} to&nbsp;finish the
                      microphone test.
                    </>
                  )
                ) : (
                  <>Finishing the microphone test…</>
                )}
              </Typography>

              <Box py={0.5}>
                <WaveVolumeIndicator
                  volume={volume}
                  isMicActive={isMicActive}
                  maxHeight={64}
                  historyLength={90}
                  width="100%"
                  recordingState={RECORDING_STATE_IN_PROGRESS}
                  backgroundColor="white"
                  barColor={success[700]}
                  border="white"
                  barWidth="8px"
                />
              </Box>
            </>
          )}

          {/* Progress bar and transcript persist from recording into success */}
          {(testStatus === "recording" || testStatus === "success") && (
            <>
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{ height: 24, borderRadius: 6, opacity: 0.85 }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={0.75}
                >
                  <Typography variant="body2" color="text.secondary">
                    {wordCount > WORD_THRESHOLD
                      ? `${wordCount} ${wordsNoun(wordCount)} detected`
                      : `${wordCount} / ${WORD_THRESHOLD} ${wordsNoun(WORD_THRESHOLD)} detected`}
                  </Typography>
                  {testStatus === "recording" && (
                    <Typography variant="caption" color="text.secondary">
                      {secondsRemaining}s remaining
                    </Typography>
                  )}
                </Stack>
              </Box>

              {testStatus !== "success" && (
                <Typography
                  variant="body3"
                  color={neutral[700]}
                  sx={{
                    fontStyle: "italic",
                    minHeight: 40,
                    lineHeight: 1.4,
                    fontSize: "16px"
                  }}
                >
                  {transcriptText || "Keep talking…"}
                </Typography>
              )}
            </>
          )}

          {testStatus === "recording" && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Button variant="text" size="small" onClick={handleBack}>
                ← Change microphone
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleStop}
              >
                Stop
              </Button>
            </Stack>
          )}

          {testStatus === "stopped" && (
            <>
              <Typography variant="body2" color="text.secondary">
                Test stopped. Click "Try Again" when you're ready.
              </Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Button variant="text" size="small" onClick={handleBack}>
                  ← Change microphone
                </Button>
                <Button variant="contained" size="small" onClick={handleRetry}>
                  Try Again
                </Button>
              </Stack>
            </>
          )}

          {testStatus === "success" && (
            <>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Alert severity="success">
                  <Typography variant="body2">
                    Your microphone is working correctly. Setup is complete.
                  </Typography>
                </Alert>
              </Stack>

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleDialogClose.bind(null, {}, "closeClick")}
                >
                  Close
                </Button>
              </Stack>
            </>
          )}

          {testStatus === "failed" && (
            <>
              <Alert severity="warning">
                No clear speech was detected. Please check that your microphone
                is connected and that browser microphone permissions are
                enabled, then try again.
              </Alert>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Button variant="text" size="small" onClick={handleBack}>
                  ← Change microphone
                </Button>
                <Stack direction="row" gap={1.5}>
                  <Button variant="text" size="small" onClick={onClose}>
                    Skip for now
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleRetry}
                  >
                    Try Again
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Dialog>
  );
}
